<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { FileText, X, Loader2, AlertCircle, RotateCcw } from 'lucide-vue-next'
import { useWebChatStore } from '~/stores/web-chat'
import { isItemPending } from '~~/shared/items/itemPending'

const { t } = useI18n()
const webChat = useWebChatStore()
const { contextDocuments } = storeToRefs(webChat)

function isError(status?: string | null): boolean {
  return status === 'error'
}

function fileExt(name: string | null): string {
  if (!name) return 'DOC'
  const dot = name.lastIndexOf('.')
  return dot > 0 ? name.slice(dot + 1).toUpperCase() : 'DOC'
}

function extColor(ext: string): string {
  if (ext === 'PDF') return 'bg-destructive/15 text-destructive'
  if (ext === 'DOCX') return 'bg-info/15 text-info'
  return 'bg-muted text-muted-foreground'
}
</script>

<template>
  <div v-if="contextDocuments.length > 0" class="flex flex-wrap gap-2">
    <div
      v-for="doc in contextDocuments"
      :key="doc.id"
      class="group flex items-center gap-2 pl-2 pr-1.5 py-1.5 rounded-lg border bg-background hover:bg-accent-lighter transition-colors"
      :class="isError(doc.status) && 'border-destructive/40 bg-destructive/[0.03]'"
    >
      <span
        v-if="isItemPending(doc.status)"
        class="inline-flex items-center justify-center h-5 w-8 rounded-md shrink-0 bg-muted"
        :title="doc.status === 'uploading' ? t('documents.uploading') : t('documents.processing')"
      >
        <Loader2 class="h-3 w-3 animate-spin text-muted-foreground" />
      </span>
      <span
        v-else-if="isError(doc.status)"
        class="inline-flex items-center justify-center h-5 w-8 rounded-md shrink-0 bg-destructive/15"
        :title="t('webChat.contextDocError')"
      >
        <AlertCircle class="h-3 w-3 text-destructive" />
      </span>
      <span
        v-else
        class="inline-flex items-center justify-center h-5 w-8 text-[9px] font-bold rounded-md shrink-0"
        :class="extColor(fileExt(doc.file_name))"
      >
        {{ fileExt(doc.file_name) }}
      </span>
      <span class="text-xs font-medium truncate max-w-[180px]" :class="isError(doc.status) && 'text-destructive'">{{ doc.name }}</span>
      <!-- Re-uploaded bytes we already held: the upload was refused, this is the existing item. -->
      <span
        v-if="doc.duplicate"
        class="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground whitespace-nowrap"
        :title="t('webChat.contextDocDuplicateTitle')"
      >
        <RotateCcw class="h-3 w-3" />
        {{ t('webChat.contextDocDuplicate') }}
      </span>
      <button
        type="button"
        class="h-5 w-5 flex items-center justify-center rounded-md text-muted-foreground/50 hover:text-foreground hover:bg-accent-lighter transition-all"
        @click="webChat.detachDocument(doc.id)"
      >
        <X class="h-3.5 w-3.5" />
      </button>
    </div>
  </div>
</template>
